import { QuickButton } from '@southfields-digital/mxmz-react-ui';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StateType } from 'src/redux/reducers';
import {
  REQUEST_ANIMATE_IN_LIVE_GRAPHIC,
  REQUEST_ANIMATE_OUT_LIVE_GRAPHIC,
} from 'src/redux/reducers/control';

import { useRundownLayout } from '../RundownSettings/useRundownLayout';

import styles from './QuickControl.module.scss';
import { getNameDerivedQuickControlSubtitle, getNameDerivedQuickControlTitle } from './utils';

const MIN_PLACEHOLDER_ITEMS = 9;
const ITEMS_PER_ROW = 3;

interface IQuickControlProps {
  items?: QuickControlGraphic[];
  isFullWidth?: boolean;
  className?: string;
}

const QuickControl = ({ items, isFullWidth, className }: IQuickControlProps) => {
  const dispatch = useDispatch();

  const { layout } = useRundownLayout();

  const { playlistGraphics } = useSelector((state: StateType) => state.playlistGraphic);

  const isNameDerivedContent = ['quickcontrol', 'playlist'].includes(layout ?? '');
  const isSquareButtons = layout === 'quickcontrol';

  const quickControls = useMemo(() => {
    if (items) return items;

    return (
      playlistGraphics
        ?.filter((graphic) => graphic.isQuick)
        .map((item) => ({
          id: item.id,
          title: isNameDerivedContent ? getNameDerivedQuickControlTitle(item.name) : item.name,
          subtitle: isNameDerivedContent
            ? getNameDerivedQuickControlSubtitle(item.name, item.playlist?.name)
            : item.playlist?.name?.trim(),
          isLive: item.status.live,
        })) || []
    );
  }, [items, playlistGraphics, isNameDerivedContent]);

  // Always show $MIN_PLACEHOLDER_ITEMS, rows after have at least $ITEMS_PER_ROW
  const totalPlaceholerItems = useMemo(() => {
    const remainingItems = MIN_PLACEHOLDER_ITEMS - quickControls.length;
    if (remainingItems > 0) return remainingItems;
    if (remainingItems === 0) return 0;
    return (ITEMS_PER_ROW - (quickControls.length % ITEMS_PER_ROW)) % ITEMS_PER_ROW;
  }, [quickControls]);

  const liveIn = (item: QuickControlGraphic) => {
    if (!item) return;

    dispatch({
      type: REQUEST_ANIMATE_IN_LIVE_GRAPHIC,
      payload: { playlistGraphicId: item.id },
    });
  };

  const liveOut = (item: QuickControlGraphic) => {
    if (!item) return;

    dispatch({
      type: REQUEST_ANIMATE_OUT_LIVE_GRAPHIC,
      payload: { playlistGraphicId: item.id },
    });
  };

  const handleToggleLive = (item: QuickControlGraphic) => {
    if (!item) return;

    if (item.isLive) {
      liveOut(item);
    } else {
      liveIn(item);
    }
  };

  return (
    <div
      className={classNames(
        styles.Container,
        {
          [styles.FullWidth]: isFullWidth,
        },
        className
      )}
    >
      {quickControls.map((item: QuickControlGraphic) => (
        <QuickButton
          key={item.id}
          className={classNames(styles.QuickControlBtn, {
            [styles.SquareButtons]: isSquareButtons,
          })}
          onClick={() => handleToggleLive(item)}
          title={item.title}
          subtitle={item.subtitle}
          isRunning={item.isLive}
        />
      ))}
      {[...Array(totalPlaceholerItems).keys()].map((i) => (
        <QuickButton
          key={i}
          className={classNames(styles.QuickControlBtn, {
            [styles.SquareButtons]: isSquareButtons,
          })}
          isPlaceholder
        />
      ))}
    </div>
  );
};

export default QuickControl;
